// Team management page — multi-workspace + RBAC (admin / editor / viewer / billing)
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Users, UserPlus, Shield, Edit3, Eye, CreditCard, Trash2, Building2 } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { value: "admin", label: "Admin", desc: "Tout accès, gestion des membres et facturation", icon: Shield, color: "bg-black text-white" },
  { value: "editor", label: "Éditeur", desc: "Créer et modifier les workflows", icon: Edit3, color: "bg-emerald-100 text-emerald-700" },
  { value: "viewer", label: "Lecteur", desc: "Consultation seule des workflows et logs", icon: Eye, color: "bg-zinc-100 text-zinc-700" },
  { value: "billing", label: "Facturation", desc: "Gérer abonnement et factures", icon: CreditCard, color: "bg-amber-100 text-amber-700" },
];

const roleMeta = (r) => ROLES.find((x) => x.value === r) || ROLES[2];

export default function Team() {
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWs, setCurrentWs] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("editor");
  const [showInvite, setShowInvite] = useState(false);
  const [newWsName, setNewWsName] = useState("");
  const [showNewWs, setShowNewWs] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    const ws = (await api.get("/workspaces")).data;
    setWorkspaces(ws);
    if (ws.length > 0) {
      const sel = currentWs ? ws.find((w) => w.id === currentWs.id) || ws[0] : ws[0];
      setCurrentWs(sel);
      const mems = (await api.get(`/workspaces/${sel.id}/members`)).data;
      setMembers(mems);
    }
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  const switchWs = async (ws) => {
    setCurrentWs(ws);
    const mems = (await api.get(`/workspaces/${ws.id}/members`)).data;
    setMembers(mems);
  };

  const invite = async () => {
    if (!inviteEmail.trim()) return toast.error("Email requis");
    try {
      const { data } = await api.post(`/workspaces/${currentWs.id}/invite`, { email: inviteEmail, role: inviteRole });
      toast.success(data.existing_user ? "Membre ajouté" : "Invitation envoyée (en attente d'inscription)");
      setInviteEmail("");
      setShowInvite(false);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'invitation");
    }
  };

  const changeRole = async (userId, role) => {
    try {
      await api.patch(`/workspaces/${currentWs.id}/members/${userId}`, { role });
      toast.success("Rôle mis à jour");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const removeMember = async (userId) => {
    if (!window.confirm("Retirer ce membre ?")) return;
    try {
      await api.delete(`/workspaces/${currentWs.id}/members/${userId}`);
      toast.success("Membre retiré");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const createWs = async () => {
    if (!newWsName.trim()) return toast.error("Nom requis");
    try {
      const { data } = await api.post("/workspaces", { name: newWsName });
      toast.success(`Espace "${data.name}" créé`);
      setNewWsName("");
      setShowNewWs(false);
      loadAll();
    } catch (e) {
      toast.error("Échec création");
    }
  };

  const myRole = currentWs?.role || "viewer";
  const isAdmin = myRole === "admin";

  if (loading) return <div className="p-8 text-sm text-zinc-500">Chargement...</div>;

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="team-page">
      <div className="flex items-start justify-between mb-10">
        <div>
          <span className="overline">Espaces & équipe</span>
          <h1 className="font-display text-4xl font-medium tracking-tighter mt-2">Vos espaces de travail</h1>
          <p className="text-zinc-600 mt-2">Collaborez en équipe sur les mêmes workflows. 4 rôles disponibles.</p>
        </div>
        <Button onClick={() => setShowNewWs(true)} className="rounded-sm bg-black text-white hover:bg-zinc-800" data-testid="new-workspace-btn">
          <Building2 size={14} className="mr-2"/> Nouvel espace
        </Button>
      </div>

      {/* New workspace form */}
      {showNewWs && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-zinc-200 rounded-sm p-6 mb-6 flex gap-3 items-end">
          <div className="flex-1">
            <Label htmlFor="ws-name">Nom de l'espace</Label>
            <Input id="ws-name" value={newWsName} onChange={(e) => setNewWsName(e.target.value)} placeholder="Ex: Cabinet Dupont" className="rounded-sm mt-2"/>
          </div>
          <Button onClick={createWs} className="rounded-sm bg-black text-white" data-testid="confirm-new-workspace">Créer</Button>
          <Button onClick={() => setShowNewWs(false)} variant="outline" className="rounded-sm">Annuler</Button>
        </motion.div>
      )}

      {/* Workspaces list */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        {workspaces.map((ws) => {
          const isActive = currentWs?.id === ws.id;
          const meta = roleMeta(ws.role);
          return (
            <motion.button
              key={ws.id}
              onClick={() => switchWs(ws)}
              whileHover={{ y: -2 }}
              className={`text-left p-5 rounded-sm border transition-all ${
                isActive ? "border-black bg-black text-white" : "border-zinc-200 bg-white"
              }`}
              data-testid={`workspace-card-${ws.id}`}
            >
              <Building2 size={18} className={isActive ? "text-white mb-2" : "text-zinc-500 mb-2"}/>
              <p className="font-display font-medium tracking-tight truncate">{ws.name}</p>
              <Badge className={`mt-3 rounded-sm font-mono text-[10px] ${isActive ? "bg-white text-black" : meta.color}`}>{meta.label.toUpperCase()}</Badge>
            </motion.button>
          );
        })}
      </div>

      {/* Members */}
      {currentWs && (
        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="p-6 border-b border-zinc-200 flex items-center justify-between">
            <div>
              <span className="overline">{currentWs.name}</span>
              <h2 className="font-display text-xl font-medium tracking-tight mt-1">Membres ({members.length})</h2>
            </div>
            {isAdmin && (
              <Button onClick={() => setShowInvite(true)} className="rounded-sm bg-emerald-600 text-white hover:bg-emerald-700" data-testid="invite-member-btn">
                <UserPlus size={14} className="mr-2"/> Inviter
              </Button>
            )}
          </div>

          {showInvite && isAdmin && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-6 bg-zinc-50 border-b border-zinc-200 flex gap-3 items-end">
              <div className="flex-1">
                <Label htmlFor="invite-email">Email</Label>
                <Input id="invite-email" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="collaborateur@entreprise.eu" className="rounded-sm mt-2" data-testid="invite-email-input"/>
              </div>
              <div className="w-44">
                <Label>Rôle</Label>
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger className="rounded-sm mt-2" data-testid="invite-role-select"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={invite} className="rounded-sm bg-black text-white" data-testid="confirm-invite">Inviter</Button>
              <Button onClick={() => setShowInvite(false)} variant="outline" className="rounded-sm">Annuler</Button>
            </motion.div>
          )}

          <ul className="divide-y divide-zinc-200">
            {members.map((m) => {
              const meta = roleMeta(m.role);
              const RIcon = meta.icon;
              return (
                <li key={m.user_id} className="p-5 flex items-center justify-between" data-testid={`member-row-${m.user_id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full klein-bg text-white flex items-center justify-center font-display font-medium">
                      {m.full_name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{m.full_name}</p>
                      <p className="text-xs text-zinc-500">{m.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isAdmin ? (
                      <Select value={m.role} onValueChange={(v) => changeRole(m.user_id, v)}>
                        <SelectTrigger className="rounded-sm w-36 h-8 text-xs" data-testid={`role-select-${m.user_id}`}><SelectValue/></SelectTrigger>
                        <SelectContent>
                          {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={`${meta.color} rounded-sm font-mono text-[10px]`}><RIcon size={10} className="mr-1"/> {meta.label.toUpperCase()}</Badge>
                    )}
                    {isAdmin && (
                      <Button onClick={() => removeMember(m.user_id)} variant="outline" className="rounded-sm border-zinc-300 text-red-600 hover:bg-red-50 h-8 w-8 p-0">
                        <Trash2 size={12}/>
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Role legend */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {ROLES.map((r) => {
          const Icon = r.icon;
          return (
            <div key={r.value} className="bg-white border border-zinc-200 rounded-sm p-5">
              <div className={`w-9 h-9 ${r.color} rounded-sm flex items-center justify-center mb-3`}>
                <Icon size={16}/>
              </div>
              <p className="font-display font-medium tracking-tight mb-1">{r.label}</p>
              <p className="text-xs text-zinc-600">{r.desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
